import { type ApolloClient } from '@apollo/client';
import { logError } from 'fergy-core-react-logging';
import { type ErrorFieldsFragment } from '../../../client/__generated__/graphql-client-types';
import { GENERIC_ERROR } from '../../constants/message';
import { useNotification } from '../notification/notification.hooks';

const isErrorResult = (value: any): value is ErrorFieldsFragment => {
	return value?.__typename === 'Error';
};

const findErrorInResult = (result: ApolloClient.MutateResult): ErrorFieldsFragment | undefined => {
	return Object.values(result.data || {}).find(isErrorResult);
};

const findSuccessDataInResult = (result: ApolloClient.MutateResult) => {
	return Object.values(result.data || {}).find((value) => !isErrorResult(value));
};

export type HandleResultOptions<TSuccessData = unknown> = {
	successMessage?: string;
	genericErrorMessage?: string;
	errorMessageHideTimeout?: number;
	onError?: (message: string, code: string) => void; // called if mutation returns a `Error` type
	onApolloError?: (message: string) => void; // called if response contains Apollo errors
	onSuccess?: (data: TSuccessData) => void; // called if response contained no errors, receives the non-error result data
	errorCallback?: () => void; // callback fired within onError and onApolloError
};

/**
 * Hook meant to simplify handling of logging and notification of errors/success states
 * in a mutation result
 */
export function useHandleMutationResult() {
	const { notifyDanger, notifySuccess } = useNotification();

	return {
		/**
		 * Handle errors/success in a mutation fetch result
		 */
		handleResult: <TSuccessData = unknown>(result: ApolloClient.MutateResult, options: HandleResultOptions<TSuccessData> = {}) => {
			const { successMessage, genericErrorMessage, errorMessageHideTimeout, errorCallback } = options;
			const optDefaults: Omit<
				Required<HandleResultOptions<TSuccessData>>,
				'errorCallback' | 'successMessage' | 'errorMessageHideTimeout'
			> = {
				genericErrorMessage: GENERIC_ERROR,
				onError: (errMessage) => {
					notifyDanger(errMessage, errorMessageHideTimeout);
					if (errorCallback) {
						errorCallback();
					}
				},
				onApolloError: (apolloMessage) => {
					notifyDanger(genericErrorMessage || GENERIC_ERROR, errorMessageHideTimeout);
					logError(apolloMessage);
					if (errorCallback) {
						errorCallback();
					}
				},
				onSuccess: () => {
					if (successMessage) {
						notifySuccess(successMessage);
					}
				}
			};

			const { onError, onApolloError, onSuccess } = Object.assign({}, optDefaults, options);

			const error: ErrorFieldsFragment | undefined = findErrorInResult(result);

			let isError = false;
			let code: string | undefined;
			let message: string | undefined;

			if (error?.__typename === 'Error') {
				// Error type returned
				isError = true;
				code = error.code;
				message = error.message;
				onError(message, code);
			} else if (result.error) {
				// Apollo error
				isError = true;
				onApolloError(error?.message || result.error.message);
			}

			if (!isError) {
				onSuccess(findSuccessDataInResult(result));
			}

			return {
				isError,
				code,
				message
			};
		},
		/**
		 * Handle exception (promise reject) from mutation call
		 */
		handleException: (error?: unknown) => {
			notifyDanger(GENERIC_ERROR);
			if (error) {
				logError(error);
			}
		}
	};
}
